<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue'

defineOptions({
  name: 'AppCombobox',
  inheritAttrs: false,
})

type ComboboxOption = {
  value: string | number
  label: string
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    modelValue: string | number | ''
    label?: string
    labelTooltip?: string
    id?: string
    name?: string
    placeholder?: string
    required?: boolean
    disabled?: boolean
    density?: 'default' | 'compact'
    options?: ComboboxOption[]
    error?: string | string[] | null
    emptyLabel?: string
  }>(),
  {
    modelValue: '',
    required: false,
    disabled: false,
    density: 'default',
    options: () => [],
    error: null,
    emptyLabel: 'Inga tr&auml;ffar.',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const uid = useId()
const elementId = computed(() => props.id || uid)
const listId = computed(() => `${elementId.value}-list`)
const errorId = computed(() => `${elementId.value}-error`)

const errorMessage = computed(() => {
  if (!props.error) return ''
  if (Array.isArray(props.error)) return props.error.find(Boolean) ?? ''
  return props.error
})

const hasError = computed(() => Boolean(errorMessage.value))

const attrs = useAttrs() as Record<string, unknown>
const wrapperClass = computed(() => [
  'app-field',
  props.density === 'compact' ? 'app-field--compact' : undefined,
  attrs.class,
])

const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

const root = ref<HTMLElement | null>(null)
const query = ref('')
const isOpen = ref(false)
const activeIndex = ref(-1)

const selectedOption = computed(() => {
  const value = props.modelValue
  if (value === '' || value === null || value === undefined) return null
  const valueString = String(value)
  return props.options.find((option) => String(option.value) === valueString) || null
})

const filteredOptions = computed(() => {
  const term = query.value.trim().toLowerCase()
  if (!term) return props.options
  return props.options.filter((option) => {
    const label = String(option.label || '').toLowerCase()
    const value = String(option.value || '').toLowerCase()
    return label.includes(term) || value.includes(term)
  })
})

const activeOptionId = computed(() => {
  if (activeIndex.value < 0) return undefined
  return `${elementId.value}-option-${activeIndex.value}`
})

function syncQueryToSelection() {
  query.value = selectedOption.value?.label || ''
}

function openList() {
  if (props.disabled) return
  isOpen.value = true
}

function closeList() {
  isOpen.value = false
  activeIndex.value = -1
}

function findNextIndex(start: number, delta: number) {
  const options = filteredOptions.value
  if (!options.length) return -1
  let idx = start
  for (let i = 0; i < options.length; i += 1) {
    idx = (idx + delta + options.length) % options.length
    if (!options[idx].disabled) return idx
  }
  return -1
}

function setActiveIndexFromSelection() {
  if (!filteredOptions.value.length) {
    activeIndex.value = -1
    return
  }

  if (!selectedOption.value) {
    activeIndex.value = findNextIndex(-1, 1)
    return
  }

  const idx = filteredOptions.value.findIndex(
    (option) => String(option.value) === String(selectedOption.value?.value),
  )
  activeIndex.value = idx >= 0 ? idx : findNextIndex(-1, 1)
}

function selectOption(option: ComboboxOption) {
  if (option.disabled) return
  emit('update:modelValue', String(option.value))
  query.value = option.label
  closeList()
}

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value
  query.value = value
  openList()

  if (props.modelValue && value !== selectedOption.value?.label) {
    emit('update:modelValue', '')
  }
}

function onFocus() {
  if (props.disabled) return
  openList()
  setActiveIndexFromSelection()
}

function onKeydown(event: KeyboardEvent) {
  if (props.disabled) return

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (!isOpen.value) openList()
    activeIndex.value = findNextIndex(activeIndex.value, 1)
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (!isOpen.value) openList()
    activeIndex.value = findNextIndex(activeIndex.value, -1)
    return
  }

  if (event.key === 'Enter') {
    if (!isOpen.value) {
      openList()
      return
    }
    event.preventDefault()
    const option = filteredOptions.value[activeIndex.value]
    if (option) selectOption(option)
    return
  }

  if (event.key === 'Escape') {
    event.preventDefault()
    closeList()
    syncQueryToSelection()
  }
}

function onDocumentClick(event: MouseEvent) {
  if (!root.value) return
  if (!root.value.contains(event.target as Node)) {
    closeList()
    syncQueryToSelection()
  }
}

watch(
  () => props.modelValue,
  () => {
    syncQueryToSelection()
  },
  { immediate: true },
)

watch(
  () => props.options,
  () => {
    syncQueryToSelection()
  },
)

onMounted(() => {
  document.addEventListener('mousedown', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentClick)
})
</script>

<template>
  <div :class="wrapperClass" ref="root">
    <label v-if="label" class="app-field__label" :for="elementId">
      {{ label }}
      <span
        v-if="labelTooltip"
        class="app-field__label-icon"
        :title="labelTooltip"
        :aria-label="labelTooltip"
      >
        <AppIcon name="hero-information-circle" size="sm" />
      </span>
    </label>

    <div class="app-combobox">
      <input
        v-bind="inputAttrs"
        :id="elementId"
        class="app-input"
        :class="{
          'app-input--error': hasError,
          'app-input--compact': density === 'compact',
        }"
        :name="name"
        type="text"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :value="query"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isOpen ? 'true' : 'false'"
        :aria-controls="listId"
        :aria-activedescendant="activeOptionId"
        :aria-invalid="hasError ? 'true' : undefined"
        :aria-describedby="hasError ? errorId : undefined"
        @input="onInput"
        @focus="onFocus"
        @keydown="onKeydown"
      >

      <div
        v-if="isOpen"
        :id="listId"
        class="app-combobox__list"
        role="listbox"
      >
        <div
          v-for="(option, index) in filteredOptions"
          :id="`${elementId}-option-${index}`"
          :key="String(option.value)"
          class="app-combobox__option"
          :class="{
            'app-combobox__option--active': index === activeIndex,
            'app-combobox__option--selected': String(option.value) === String(modelValue),
            'app-combobox__option--disabled': option.disabled,
          }"
          role="option"
          :aria-selected="String(option.value) === String(modelValue) ? 'true' : 'false'"
          @mousedown.prevent="selectOption(option)"
        >
          {{ option.label }}
        </div>

        <div v-if="!filteredOptions.length" class="app-combobox__empty" v-html="emptyLabel" />
      </div>
    </div>

    <p v-if="hasError" :id="errorId" class="app-field__error">
      {{ errorMessage }}
    </p>
  </div>
</template>
